import React, { createContext, useCallback, useState, useEffect } from "react";
import FieldPulse from "~/services/fieldpulse";
import Engage from "~/services/engage";
import useDraft from "~/hooks/useDraft";
import { filterCustomerWithRelations } from "../helpers/customers";

const ContactContext = createContext({
  loading: false,
  saving: false,
  error: false,
  contact: null,
});

export function ContactProvider({ value = {}, children }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [contact, setContact] = useState(value);

  const { draft, dirty, updateDraft } = useDraft(contact);

  // Validates customer data before create and updates
  const validate = useCallback(() => {
    if (!draft.first_name) {
      setError("First Name is required.");
      return false;
    }
    if (!draft.phone) {
      setError("Phone Number is required.");
      return false;
    }
    return true;
  }, [draft, setError]);

  const getContact = useCallback(async () => {
    if (loading) return { response: contact };
    setLoading(true);
    const res = await FieldPulse.getCustomer(
      contact?.id,
      "invoices,jobs,files"
    );
    if (!res.error) {
      setContact(filterCustomerWithRelations(res.response));
    } else {
      setError(res.message);
    }
    setLoading(false);
    return res;
  }, [contact, loading, setError, setContact, setSaving]);

  // Creates customer record in FieldPulse application
  const createContact = useCallback(async () => {
    if (!validate() || saving) return { error: true };
    setSaving(true);
    const customerRes = await FieldPulse.createCustomer(draft);
    if (!customerRes.error) {
      const customer = customerRes.response;
      // Create or Update Participant record in Engage
      await Engage.createParticipant({
        first_name: customer.first_name,
        last_name: customer.last_name,
        phone_number: customer.phone,
        phone_e164: customer.phone_e164,
        remote_id: customer.id,
        email: customer.email,
      });
      // End
      setContact(filterCustomerWithRelations(customer));
    } else {
      setError(customerRes.message);
    }
    setSaving(false);
    return customerRes;
  }, [draft, saving, validate, setError, setContact, setSaving]);

  const updateContact = useCallback(
    (data) => updateDraft(data),
    [contact, updateDraft]
  );

  // Updates customer record in FieldPulse application
  const saveContact = useCallback(
    async (engageParticipantId) => {
      if (saving) return;
      setSaving(true);

      const res = await FieldPulse.updateCustomer({
        id: draft.id,
        first_name: draft.first_name,
        last_name: draft.last_name,
        phone_number: draft.phone,
        phone: draft.phone,
        phone_e164: draft.phone_e164,
        alt_phone: draft.alt_phone,
        email: draft.email,
      });

      if (!res.error) {
        const customer = res.response;

        try {
          // Update Participant record in Engage
          await Engage.updateParticipant(engageParticipantId, {
            first_name: customer.first_name,
            last_name: customer.last_name,
            phone_number: customer.phone,
            phone_e164: customer.phone_e164,
            remote_id: customer.id,
            email: customer.email,
          });
        } catch (err) {
          console.log("[ContactContext:saveContact]", err);
        }

        setContact(res.response);
      } else {
        setError(message);
      }
      setSaving(false);
      return res;
    },
    [draft, saving, setSaving, setError, setContact]
  );

  // Updates customer record in FieldPulse application
  const deleteContact = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    const res = await FieldPulse.deleteCustomer(contact.id);
    if (res.error) {
      setError(message);
    }
    setSaving(false);
    return res;
  }, [contact, saving, setSaving, setError]);

  // useEffect(() => {
  //   if (contact && contact.id) {
  //     console.log('contact_id',contact.id)
  //     getContact();
  //   }
  // }, [contact.id]);

  return (
    <ContactContext.Provider
      value={{
        contact: {
          ...contact,
          ...draft,
        },
        dirty,
        loading,
        saving,
        error,
        getContact,
        setContact,
        createContact,
        updateContact,
        saveContact,
        deleteContact,
      }}
    >
      {children}
    </ContactContext.Provider>
  );
}

export default ContactContext;
